import { uiText } from '@/i18n/interface';
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

export const useSessionManagement = () => {
  const { profile } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [promotionSessions, setPromotionSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const { toast } = useToast();

  const fetchSessions = async () => {
    if (!profile?.dojo_id) {
      console.log('❌ Missing dojo_id:', profile?.dojo_id);
      return;
    }

    console.log('📅 Fetching sessions for dojo:', profile.dojo_id);

    try {
      setIsLoading(true);

      const { data: sessionsData, error: sessionsError } = await supabase
        .from('training_sessions')
        .select('*')
        .eq('dojo_id', profile.dojo_id)
        .gte('date', new Date().toISOString().split('T')[0])
        .order('date', { ascending: true })
        .order('start_time', { ascending: true });

      if (sessionsError) {
        console.error('Error fetching sessions for dojo:', profile.dojo_id, sessionsError);
        throw sessionsError;
      }

      const { data: attendanceData, error: attendanceError } = await supabase
        .from('session_attendance')
        .select('session_id, attended')
        .eq('student_id', profile.id);

      if (!attendanceError && attendanceData) {
        const attendanceMap = new Map();

        attendanceData.forEach((att) => {
          attendanceMap.set(att.session_id, att.attended);
        });

        const sessionsWithResponses = (sessionsData || []).map((session) => ({
          ...session,
          user_response: attendanceMap.get(session.id) || null
        }));

        console.log(
          '✅ Sessions fetched for dojo:',
          profile.dojo_id,
          'Count:',
          sessionsWithResponses.length
        );

        setSessions(sessionsWithResponses);
      } else {
        console.log(
          '✅ Sessions fetched for dojo:',
          profile.dojo_id,
          'Count:',
          sessionsData?.length || 0
        );

        setSessions(sessionsData || []);
      }

      const { data: promotionData, error: promotionError } = await supabase
        .from('promotion_sessions')
        .select('*')
        .eq('dojo_id', profile.dojo_id)
        .gte('date', new Date().toISOString().split('T')[0])
        .order('date', { ascending: true });

      if (promotionError) {
        console.error('Error fetching promotion sessions:', promotionError);
      } else {
        console.log('✅ Promotion sessions fetched:', promotionData?.length || 0);
        setPromotionSessions(promotionData || []);
      }
    } catch (error) {
      console.error('Error in fetchSessions:', error);

      toast({
        title: uiText("Error"),
        description: uiText("Failed to load training sessions. Please try again."),
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();

    const handleRefresh = () => fetchSessions();
    window.addEventListener('refreshSessions', handleRefresh);

    return () => {
      window.removeEventListener('refreshSessions', handleRefresh);
    };
  }, [profile?.dojo_id]);

  const confirmAttendance = async (sessionId: string, studentId: string) => {
    try {
      const { error } = await supabase
        .from('session_attendance')
        .upsert([
          {
            session_id: sessionId,
            student_id: studentId,
            attended: true
          }
        ]);

      if (error) {
        console.error('Error confirming attendance:', error);
        throw error;
      }

      await fetchSessions();

      toast({
        title: uiText("Attendance confirmed!"),
        description: uiText("The student has been marked as present."),
      });
    } catch (error) {
      console.error('Failed to confirm attendance:', error);

      toast({
        title: uiText("Something went wrong!"),
        description: uiText("Failed to confirm attendance."),
        variant: 'destructive',
      });
    }
  };

  const confirmPromotionAttendance = async (sessionId: string, studentId: string) => {
    try {
      const { error } = await supabase
        .from('promotion_attendance')
        .upsert([
          {
            session_id: sessionId,
            student_id: studentId,
            attended: true
          }
        ]);

      if (error) {
        console.error('Error confirming promotion attendance:', error);
        throw error;
      }

      await fetchSessions();

      toast({
        title: uiText("Promotion Attendance confirmed!"),
        description: uiText("The student has been marked as present."),
      });
    } catch (error) {
      console.error('Failed to confirm promotion attendance:', error);

      toast({
        title: uiText("Something went wrong!"),
        description: uiText("Failed to confirm promotion attendance."),
        variant: 'destructive',
      });
    }
  };

  const deleteSession = async (sessionId: string) => {
    try {
      const { error } = await supabase
        .from('training_sessions')
        .delete()
        .eq('id', sessionId);

      if (error) {
        console.error('Error deleting session:', error);
        throw error;
      }

      await fetchSessions();

      toast({
        title: uiText("Session deleted!"),
        description: uiText("The session has been successfully deleted."),
      });
    } catch (error) {
      console.error('Failed to delete session:', error);

      toast({
        title: uiText("Something went wrong!"),
        description: uiText("Failed to delete the session."),
        variant: 'destructive',
      });
    }
  };

  const closePastSessions = async () => {
    if (!profile?.dojo_id) {
      toast({
        title: uiText("Error"),
        description: uiText("Missing dojo information."),
        variant: 'destructive',
      });
      return;
    }

    try {
      const today = new Date().toISOString().split('T')[0];
      const currentTime = new Date().toTimeString().slice(0, 5);

      const { error: pastDatesError } = await supabase
        .from('training_sessions')
        .update({ status: 'completed' })
        .eq('dojo_id', profile.dojo_id)
        .lt('date', today);

      if (pastDatesError) {
        console.error('Error closing past sessions:', pastDatesError);
        throw pastDatesError;
      }

      const { error: todayError } = await supabase
        .from('training_sessions')
        .update({ status: 'completed' })
        .eq('dojo_id', profile.dojo_id)
        .eq('date', today)
        .lt('end_time', currentTime);

      if (todayError) {
        console.error('Error closing today sessions:', todayError);
        throw todayError;
      }

      await fetchSessions();

      toast({
        title: uiText("Sessions updated"),
        description: uiText("Past sessions have been closed successfully."),
      });
    } catch (error) {
      console.error('Error closing past sessions:', error);

      toast({
        title: uiText("Error"),
        description: uiText("Failed to close past sessions."),
        variant: 'destructive',
      });
    }
  };

  return {
    sessions,
    promotionSessions,
    isLoading,

    fetchSessions,

    // Important alias: TrainingSchedule.tsx expects this name.
    fetchTrainingSessions: fetchSessions,

    confirmAttendance,
    confirmPromotionAttendance,
    deleteSession,
    closePastSessions,
  };
};
